/**
 * The requirements table.
 *
 * Its own grid rather than the shared EntityTable, for ONE reason now:
 * `GET /api/requirements` does not answer the uniform entity shape. It is
 * `requirement_json`, which CARRIES `level`, `parent` and `verified_by` - the
 * three columns anybody reading a requirements list actually wants - and OMITS
 * `type_name` and `rationale`, which the registry-generated columns would show
 * as permanently blank.
 *
 * The SECOND reason is gone, and saying so matters because it read as a rule.
 * It was: EntityTable fetches for itself and cannot be handed a level or a
 * subsystem, so filtering here had to happen on the server, a client-side
 * filter over a paged listing searching only the page it holds. Issue #35
 * removed the premise rather than the concern - both tables now fetch WHOLE
 * (see PAGE_LIMIT) and filter in the browser, and both say so when the fetch
 * was capped. The concern was right; it is answered by the fetch, not by the
 * server doing the filtering.
 *
 * The columns are still taken from the registry, and still not written down
 * here: a field becomes a column when the schema describes it AND the rows
 * carry it. A registry field added to requirements tomorrow appears on its own,
 * and one the listing does not answer never becomes an empty column.
 *
 * The grid is presentational - given rows, reports a click. The screen owns the
 * fetch, because what to fetch is what the URL's filters say.
 */

import { useMemo, useRef } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import { DataGrid, gridExpandedSortedRowIdsSelector, useGridApiRef } from '@mui/x-data-grid'
import type { GridColDef, GridFilterModel, GridSortModel } from '@mui/x-data-grid'
import { heading } from '../../schema'
import type { EntityRow, KindSchema } from '../../schema'
import { MONOSPACE } from '../../theme'
import {
  WRAPPING_GRID_PROPS,
  WRAPPING_GRID_SX,
  useFittedHeight,
} from '../common/grid-layout'
import { naturalComparator, sortsNaturally } from '../common/naturalCompare'

/**
 * The order the columns read in, for the ones worth placing deliberately.
 *
 * An ORDERING, not a field list: anything the schema describes and the rows
 * carry that is not named here is appended after it, so the table cannot fall
 * behind the registry. The three that are named because nothing else would
 * produce them are `level` - which has no registry field at all, being read
 * from the type chain - and `parent` and `verified_by`, a reference and a list,
 * which the uniform listing drops to avoid a query per row and which THIS
 * listing answers directly.
 */
export const COLUMN_ORDER = [
  'level',
  'name',
  'statement',
  'status',
  'test_level',
  'segment',
  'subsystem',
  'category',
  'sources',
  'verified_by',
  'is_illustrative',
]

/** Columns the grid renders itself, so the generated ones must not repeat them. */
const ALREADY_SHOWN = new Set(['id', 'identifier', 'qualified_name', 'line'])

/** An ID or a parent ID: monospace, as everywhere else in BEST. */
function mono(value: unknown) {
  return <Box sx={{ fontFamily: MONOSPACE, fontSize: '0.85rem' }}>{String(value ?? '')}</Box>
}

/**
 * The columns for a set of requirement rows.
 *
 * Exported and pure so it can be asserted on directly: whether a column
 * appears is decided by the schema and by the rows, and getting that wrong
 * shows up as a plausible-looking table with the wrong columns in it.
 *
 * @param kind - the requirement KindSchema, for labels and field kinds
 * @param rows - the rows as GET /api/requirements answered them
 */
export function requirementColumns(kind: KindSchema, rows: EntityRow[]): GridColDef[] {
  // Which keys the rows actually carry. Presence, not truthiness: a column of
  // blanks for a field nobody has filled in is information, whereas a column
  // for a field the endpoint never answers is noise.
  const present = new Set<string>()
  for (const row of rows) {
    for (const key of Object.keys(row)) present.add(key)
  }

  const described = new Map(kind.fields.map((field) => [field.name, field]))

  // Everything the registry describes and the rows carry, in the curated order
  // first and then whatever else the registry has grown.
  const names = [
    ...COLUMN_ORDER.filter((name) => present.has(name)),
    ...kind.fields
      .map((field) => field.name)
      .filter((name) => present.has(name) && !COLUMN_ORDER.includes(name)),
  ].filter((name) => !ALREADY_SHOWN.has(name))

  return names.map((name) => {
    const field = described.get(name)
    const column: GridColDef = {
      field: name,
      headerName: field?.label ?? heading(name),
      description: field?.help ?? '',
      flex: 1,
      minWidth: 110,
      // Ids, names and lists of ids sort NATURALLY (issue #82). The cases
      // below that rebuild the column keep it by spreading this one.
      ...(field && sortsNaturally(field.kind) ? { sortComparator: naturalComparator } : {}),
    }

    if (field?.kind === 'doc') {
      // The statement is the point of the row, so it gets the room.
      return { ...column, flex: 3, minWidth: 280 }
    }
    if (field?.kind === 'flag' || field?.kind === 'conjugate') {
      return { ...column, type: 'boolean', flex: 0, minWidth: 90 }
    }
    if (name === 'level') {
      return { ...column, type: 'number', flex: 0, minWidth: 80, align: 'left', headerAlign: 'left' }
    }
    if (name === 'sources') {
      // A LIST since issue #32 - a requirement may flow down from several, and
      // no source is primary. Joined rather than counted, for the same reason
      // verified_by is: which requirement it derives from is the question.
      return {
        ...column,
        minWidth: 160,
        valueGetter: (value: unknown) => (Array.isArray(value) ? value.join(', ') : (value ?? '')),
        sortComparator: naturalComparator,
        renderCell: (params) => mono(params.value),
      }
    }
    if (name === 'verified_by') {
      // A list of case IDs. Joined rather than counted: which case verifies a
      // requirement is the question, and 'two' does not answer it.
      return {
        ...column,
        minWidth: 160,
        valueGetter: (value: unknown) => (Array.isArray(value) ? value.join(', ') : (value ?? '')),
        sortComparator: naturalComparator,
        renderCell: (params) => mono(params.value),
      }
    }
    return column
  })
}

export interface RequirementGridProps {
  kind: KindSchema
  rows: EntityRow[]
  loading: boolean
  /** Where a click goes. The screen decides, so the route stays in one place. */
  onOpen: (row: EntityRow) => void
  /**
   * The column filters, held by the SCREEN because they live in the URL.
   *
   * The grid would happily keep them itself, and that is the trap: held
   * internally they cannot be read, so a column-filtered table stops being a
   * link and a reload silently drops the question. The five dropdowns this
   * replaced were linkable, and nothing on screen would have said the column
   * filters were not.
   *
   * The GRID's model, not the URL's: it includes a condition still being
   * built, which the link leaves out. See useColumnFilters.
   */
  filterModel: GridFilterModel
  onFilterModelChange: (next: GridFilterModel) => void
  /**
   * The sort, held by the screen for the same reason: it is in the URL, so it
   * is part of the link and comes back with the filters (issue #86). Optional
   * only so a test of the grid alone need not hold one; the screen passes both.
   */
  sortModel?: GridSortModel
  onSortModelChange?: (next: GridSortModel) => void
  /**
   * WHICH rows the grid is actually SHOWING, reported as it changes.
   *
   * The screen's own count line knows about the search box and the quick
   * filters, which it applies itself, and knew nothing about the grid's COLUMN
   * filters, which MUI applies internally - so it read "6 of 10 requirements
   * match" above five rows. Small, and exactly the kind of thing issue #35
   * existed to remove: a screen stating a number that is not the one on screen.
   *
   * Reported from the grid rather than recomputed on the screen, because the
   * alternative is reimplementing MUI's filter operators to predict its answer -
   * a second implementation of the thing that would then have to agree with it.
   *
   * The IDS rather than a count since the export menu (issue #56): "export what
   * the filter shows" has to send the server the same rows the count line
   * counts, and the count is just the length of this list. Every filtered row,
   * not only the current page, in the grid's sort order.
   */
  onVisibleIdsChange?: (ids: string[]) => void
}

/** Same ids in the same order? Cheap exits first: this runs on every grid state change. */
function sameIds(a: string[] | null, b: string[]): boolean {
  if (a === b) return true
  if (a === null || a.length !== b.length) return false
  return a.every((id, index) => id === b[index])
}

export default function RequirementGrid({
  kind,
  rows,
  loading,
  onOpen,
  filterModel,
  onFilterModelChange,
  sortModel,
  onSortModelChange,
  onVisibleIdsChange,
}: RequirementGridProps) {
  const apiRef = useGridApiRef()
  /**
   * The last list reported, so an unchanged one is not reported again.
   *
   * `onStateChange` fires on every state change - hover, focus, scroll - and the
   * screen holds the ids in state, so reporting each time would set state to a
   * new array each time and re-render the screen (and this grid) in a loop of
   * its own making. The selector is memoized and usually hands back the SAME
   * array, so `lastSelected` settles most calls without touching the ids; a new
   * array with the same contents (MUI rebuilds it when rows are replaced) is
   * settled by `sameIds`.
   */
  const reported = useRef<string[] | null>(null)
  const lastSelected = useRef<unknown>(null)
  // The statement column is the reason this grid is wider than the window, so
  // it is the one that has to wrap and the one whose sideways scrollbar has to
  // be reachable. Both come from grid-layout, which the other listings share.
  const { ref, height } = useFittedHeight()

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        description: 'The short name in angle brackets, which is the ID an RTM reports.',
        minWidth: 150,
        sortComparator: naturalComparator,
        renderCell: (params) => mono(params.value),
      },
      ...requirementColumns(kind, rows),
    ],
    [kind, rows],
  )

  return (
    <Paper sx={{ p: 1.5 }}>
      <Box ref={ref} sx={{ height }}>
        <DataGrid
          {...WRAPPING_GRID_PROPS}
          rows={rows}
          columns={columns}
          loading={loading}
          density="compact"
          disableRowSelectionOnClick
          apiRef={apiRef}
          filterModel={filterModel}
          // Not while loading. The columns come from the ROWS, so until they
          // arrive this grid has one column, and it reports a filter on any
          // other as removed - which would take a pasted link's filter out
          // before the table it filters had been drawn. Declining the report
          // leaves the grid holding what it was given, and by the time the
          // rows are here so is the column.
          onFilterModelChange={(next) => {
            if (!loading) onFilterModelChange(next)
          }}
          sortModel={sortModel}
          // Not while loading either, and for the same reason: MUI drops a sort
          // on a column it does not have yet, and reports that as a change.
          onSortModelChange={(next) => {
            if (!loading) onSortModelChange?.(next)
          }}
          // Every state change, not just a filter one: MUI settles the filtered
          // set over more than one commit, and reporting only on filter changes
          // left the count one edit behind.
          onStateChange={() => {
            if (!onVisibleIdsChange) return
            const selected = gridExpandedSortedRowIdsSelector(apiRef)
            if (selected === lastSelected.current) return
            lastSelected.current = selected
            const ids = selected.map(String)
            if (sameIds(reported.current, ids)) return
            reported.current = ids
            onVisibleIdsChange(ids)
          }}
          onRowClick={(params) => onOpen(params.row as EntityRow)}
          initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
          pageSizeOptions={[25, 50, 100]}
          sx={{ ...WRAPPING_GRID_SX, '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        />
      </Box>
    </Paper>
  )
}
